import { useMemo, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { MediaSetting } from "@circle-react/types/MediaSetting";
import { EmptyState } from "@circle-react-uikit/EmptyState";
import { Typography } from "@circle-react-uikit/Typography";
import { i18nRoot } from "../constants";
import { useChapterThumbnails } from "../hooks/useChapterThumbnails";
import type { MediaSettingFormData } from "../types";
import { AddChapterButton } from "./AddChapterButton";
import { ChapterItem } from "./ChapterItem";

interface ChaptersListProps {
  currentTime: number;
  duration: number;
  mediaSettings: MediaSetting;
  onChapterAdd?: (time: number) => void;
  onChapterJump?: (time: number) => void;
}

export const ChaptersList = ({
  currentTime,
  duration,
  mediaSettings,
  onChapterAdd,
  onChapterJump,
}: ChaptersListProps) => {
  const [newChapterIndex, setNewChapterIndex] = useState<number | null>(null);
  const [editChapterIndex, setEditChapterIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { control, getValues } = useFormContext<MediaSettingFormData>();
  const {
    fields: chapters,
    append,
    update,
    remove,
  } = useFieldArray({
    control,
    name: "chapters",
  });

  const sortedChapters = useMemo(
    () => [...chapters].sort((a, b) => a.start_time - b.start_time),
    [chapters],
  );
  const hasChapters = chapters.length !== 0;

  const { chapterThumbnails, isLoading: isLoadingChapterThumbnails } =
    useChapterThumbnails({
      chapters,
      previewThumbnailsUrl: mediaSettings.preview_thumbnails_url,
    });

  const handleChapterAdd = () => {
    const startTime = chapters.length === 0 ? 0.0 : currentTime;

    onChapterAdd?.(startTime);
    append({
      title: t(`${i18nRoot}.chapters.untitled`),
      start_time: startTime,
    });
    setNewChapterIndex(chapters.length);
    setEditChapterIndex(chapters.length);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Typography.LabelMd weight="semibold">
          {t(`${i18nRoot}.chapters_section`)}
        </Typography.LabelMd>
        <div className="flex-1" />
        <AddChapterButton
          className={classNames({ invisible: !hasChapters })}
          chapters={chapters}
          currentTime={currentTime}
          duration={duration}
          onChapterAdd={handleChapterAdd}
        />
      </div>
      {hasChapters ? (
        <div ref={containerRef} className="flex flex-col gap-3">
          {sortedChapters.map(chapter => {
            const index = chapters.findIndex(c => c.id === chapter.id);
            const formChapter = getValues(`chapters.${index}`);

            const thumbnailUrl = isLoadingChapterThumbnails
              ? undefined
              : (chapterThumbnails[String(formChapter.start_time)] ??
                mediaSettings.default_thumbnail_url);

            return (
              <ChapterItem
                key={chapter.id}
                chapter={formChapter}
                isNew={newChapterIndex === index}
                isEditing={editChapterIndex === index}
                onEditStart={() => setEditChapterIndex(index)}
                onEditEnd={() => {
                  setNewChapterIndex(null);
                  setEditChapterIndex(null);
                }}
                onJumpToChapter={onChapterJump}
                onUpdate={value => update(index, value)}
                onRemove={() => remove(index)}
                thumbnailUrl={thumbnailUrl}
              />
            );
          })}
        </div>
      ) : (
        <EmptyState
          header={t(`${i18nRoot}.chapters.empty_state.title`)}
          description={t(`${i18nRoot}.chapters.empty_state.description`)}
          primaryLabel={t(`${i18nRoot}.chapters.empty_state.primary_label`)}
          onPrimaryClick={handleChapterAdd}
        />
      )}
    </div>
  );
};
